import { Drawer, EstimatorAvatar, IconButton, makeStyles, Menu, MenuItem, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, SvgIconProps } from '@paintscout/ui'
import classnames from 'classnames'
import AddIcon from '@material-ui/icons/Add'
import ArrowDropDownIcon from '@material-ui/icons/KeyboardArrowDown'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
// Solid, for the document section's company meta rows only — see headerIcons. Every other row here
// belongs to a contact / address / estimator section, which is secondary and stays outlined (ENG-1366).
import { EventIcon as MetaEventIcon, LocationIcon as MetaLocationIcon, PhoneIcon as MetaPhoneIcon } from './headerIcons'
// The company, as the side nav's Companies link draws it (ENG-2098) — outlined, like the rows beside
// it: the Font Awesome kit serves solid glyphs only (see headerIcons).
import BuildingsIcon from '@material-ui/icons/BusinessOutlined'
import EditIcon from '@material-ui/icons/EditOutlined'
import EventIcon from '@material-ui/icons/EventOutlined'
import LocationOnIcon from '@material-ui/icons/LocationOnOutlined'
import MailIcon from '@material-ui/icons/MailOutline'
import PeopleIcon from '@material-ui/icons/PeopleAltOutlined'
import PersonIcon from '@material-ui/icons/PersonOutline'
import PhoneIcon from '@material-ui/icons/PhoneOutlined'
import RemoveIcon from '@material-ui/icons/Remove'
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined'
import React, { useEffect, useState } from 'react'
import { getActivityColor } from '@paintscout/util'
import { activatable } from './activatable'
import { openMailto, openMap, openTel } from '../../util/deepLinks'
import { useQuoteDocumentHeaderStyles } from './quoteDocumentHeaderStyles'
import type { QuoteDocumentHeaderData } from './useQuoteDocumentHeaderData'

// The estimator dropdown chip (ENG-2080), sized from the prototype: the sheet's outlined pill as a
// button, with a 12px caret that turns while its list is open.
const useDropdownStyles = makeStyles(
  (theme: Theme) => ({
    sheetDropdown: {
      gap: 6,
      cursor: 'pointer',
      font: 'inherit',
      fontSize: theme.typography.pxToRem(14),
      textAlign: 'left',
      WebkitTapHighlightColor: 'transparent'
    },
    sheetDropdownText: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    sheetDropdownCaret: {
      fontSize: 14,
      flexShrink: 0,
      transition: 'transform 0.15s'
    },
    sheetDropdownCaretOpen: {
      transform: 'rotate(180deg)'
    },
    // The same 8px drop `sheetRowIcon` takes, since the row aligns to the top of its pill.
    sheetAvatar: {
      display: 'flex',
      flexShrink: 0,
      marginTop: 8
    }
  }),
  { name: 'QuoteDocumentHeaderMobileSheetDropdown' }
)

/** Which row of the mobile header the sheet is showing — `document` is the summary row. */
export type QuoteDocumentHeaderSection = 'document' | 'contact' | 'secondary-contact' | 'job' | 'estimator'

export interface QuoteDocumentHeaderMobileSheetProps {
  /** The open section, or `null` when the sheet is closed. */
  section: QuoteDocumentHeaderSection | null
  onClose: () => void
  data: QuoteDocumentHeaderData
  /**
   * Opened from the customer's header. The contact's name and company then open no profile: the
   * customer has no app to open it in, and the context's `onViewContact` default is a no-op.
   */
  customerFacing?: boolean
  /**
   * The work order's "hide the client's email & phone from the crew" switch (ENG-2080). The phone
   * work-order Contact row shows only the name, so the switch lives in this sheet's Edit menu.
   */
  contactDetailsToggle?: { hidden: boolean; onChange: (hidden: boolean) => void }
}

/**
 * The bottom sheet a row of {@link QuoteDocumentHeaderMobile} opens. A phone row can only show one
 * truncated value, so tapping it does not jump straight into the editor the matching desktop column
 * opens: it opens this sheet, which shows the section's full detail — every email, phone, and address,
 * each one tappable (tel: / mailto: / maps) — with that section's edit actions behind one Edit button.
 *
 * Only a row that already has a value opens the sheet. An empty row has nothing to show and goes
 * straight to its picker, so the sheet never renders an empty body.
 */
export default function QuoteDocumentHeaderMobileSheet({
  section,
  onClose,
  data,
  customerFacing,
  contactDetailsToggle
}: QuoteDocumentHeaderMobileSheetProps) {
  const classes = { ...useQuoteDocumentHeaderStyles(), ...useDropdownStyles() }
  const {
    onEditContact,
    onSelectContact,
    onViewContact,
    onSelectSecondaryContact,
    onEditSecondaryContact,
    onRemoveSecondaryContact,
    onEditJobAddress,
    onSelectEstimator,
    setEstimatorUser,
    estimatorOptions,
    isCurrentEstimator,
    isEditable,
    setDateAnchorEl,
    companyName,
    logo,
    companyAddress,
    companyPhone,
    companyAdditional,
    isWorkOrder,
    docLabel,
    quoteId,
    issued,
    expires,
    workOrderDate,
    contactName,
    contactCompany,
    onViewContactCompany,
    contactAddressLines,
    contactPhones,
    contactEmails,
    contactClickable,
    canCreateContact,
    canAddSecondaryContact,
    secondaryContactName,
    secondaryContactCompany,
    secondaryContactAddressLines,
    secondaryContactPhones,
    secondaryContactEmails,
    secondaryContactClickable,
    jobIdentifier,
    jobAddressLines,
    jobAddressClickable,
    owner,
    ownerUser,
    userPictures,
    estimatorName,
    estimatorEmail,
    estimatorPhone,
    estimatorClickable
  } = data

  // The sheet slides out over a couple hundred ms, and `section` is already null by then — without this
  // the body would blank out mid-animation. Hold the last section so the closing sheet keeps its content.
  const [shownSection, setShownSection] = useState<QuoteDocumentHeaderSection | null>(section)
  // The Edit menu anchors to a button inside the sheet, so it can never outlive it.
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  // The estimator dropdown's list, anchored to its chip.
  const [estimatorMenuAnchorEl, setEstimatorMenuAnchorEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (section) {
      setShownSection(section)
    } else {
      setMenuAnchorEl(null)
      setEstimatorMenuAnchorEl(null)
    }
  }, [section])

  // Run an action and close both layers: every action here opens a dialog of its own, so leaving the
  // sheet up behind it just stacks two layers of chrome on a phone screen.
  const act = (run: () => void) => () => {
    setMenuAnchorEl(null)
    onClose()
    run()
  }

  // The one action that does *not* close the sheet: the date editor is a pop-over, not a dialog, so it
  // anchors to the Edit button it was opened from — and that button has to still be there to anchor to.
  const editDate = () => {
    const anchor = menuAnchorEl
    setMenuAnchorEl(null)
    setDateAnchorEl(anchor)
  }

  const isDocument = shownSection === 'document'
  const sectionTitle = {
    contact: 'Prepared for',
    'secondary-contact': 'Secondary Contact',
    job: 'Job Address',
    // A work order calls the owner its Sales Rep, as its summary row does.
    estimator: isWorkOrder ? 'Sales Rep' : 'Prepared by'
  }[shownSection === 'document' ? 'contact' : shownSection ?? 'contact']

  // The date the document sheet shows: one plain document date on a work order, the created date plus
  // Expires on an estimate — the same pair the desktop header's meta row carries. The created date
  // carries no label: the calendar icon already says what it is, and the room is worth more (ENG-1367).
  const documentDate = isWorkOrder
    ? workOrderDate
    : [issued, expires && `Expires: ${expires}`].filter(Boolean).join(' • ') || null

  // One plain icon + text line, for the read-only document sheet.
  const renderPlainRow = (key: string, Icon: React.ComponentType<{ className?: string }>, value?: string | null) =>
    value ? (
      <div key={key} className={classes.sheetPlainRow}>
        <Icon className={classes.sheetPlainIcon} />
        <Typography component="span" className={classes.sheetPlainText}>
          {value}
        </Typography>
      </div>
    ) : null

  // One detail row: a 16px icon (or an avatar) beside the value in its own outlined pill. A row with an
  // action deep-links (tel: / mailto: / maps) or opens the contact profile; the rest are plain.
  const renderRow = (args: {
    key: string
    icon?: React.ComponentType<{ className?: string }>
    leading?: React.ReactNode
    /** Keep the icon's space but draw nothing — a repeat of the row above it (ENG-2035). */
    blankIcon?: boolean
    value?: string | null
    onActivate?: () => void
  }) => {
    const { key, icon: Icon, leading, blankIcon, value, onActivate } = args
    if (!value) return null
    return (
      <div key={key} className={classes.sheetRow}>
        {leading ??
          (blankIcon ? (
            <span className={classes.sheetRowIconSpacer} />
          ) : Icon ? (
            <Icon className={classes.sheetRowIcon} />
          ) : null)}
        <Typography
          component="span"
          className={classnames(classes.sheetPill, { [classes.sheetPillClickable]: !!onActivate })}
          {...activatable(!!onActivate, onActivate)}
        >
          {value}
        </Typography>
      </div>
    )
  }

  // Company, name, then the deep-linkable detail lines — the same set the desktop contact column
  // renders. Shared by the primary and secondary contact so the two sheets read identically.
  const renderContactRows = (args: {
    name: string | null
    company: string | null
    emails: string[]
    phones: string[]
    addressLines: string[]
    /** Only the primary contact has a profile to open. */
    onViewProfile?: () => void
    /** Only a primary contact's linked Company has a profile to open. */
    onViewCompany?: () => void
  }) => (
    <>
      {/* company first — it is the contact's primary line on desktop too (ENG-2033) */}
      {renderRow({ key: 'company', icon: BuildingsIcon, value: args.company, onActivate: args.onViewCompany })}
      {renderRow({ key: 'name', icon: PersonIcon, value: args.name, onActivate: args.onViewProfile })}
      {args.emails.map((email, index) =>
        renderRow({
          key: `email-${email}`,
          icon: MailIcon,
          blankIcon: index > 0,
          value: email,
          onActivate: () => openMailto(email)
        })
      )}
      {args.phones.map((phone, index) =>
        renderRow({
          key: `phone-${phone}`,
          icon: PhoneIcon,
          blankIcon: index > 0,
          value: phone,
          onActivate: () => openTel(phone)
        })
      )}
      {renderRow({
        key: 'address',
        icon: LocationOnIcon,
        value: args.addressLines.join(', ') || null,
        onActivate: args.addressLines.length > 0 ? () => openMap(args.addressLines.join(', ')) : undefined
      })}
    </>
  )

  const renderRows = () => {
    switch (shownSection) {
      case 'contact':
        return renderContactRows({
          name: contactName,
          company: contactCompany,
          emails: contactEmails,
          phones: contactPhones,
          addressLines: contactAddressLines,
          onViewProfile: customerFacing ? undefined : onViewContact,
          onViewCompany: customerFacing ? undefined : onViewContactCompany
        })
      case 'secondary-contact':
        return renderContactRows({
          // A contact saved with no name still has to be identifiable, so fall back to its email.
          name: secondaryContactName || secondaryContactEmails[0] || null,
          company: secondaryContactCompany,
          emails: secondaryContactEmails,
          phones: secondaryContactPhones,
          addressLines: secondaryContactAddressLines
        })
      case 'job':
        return (
          <>
            {renderRow({ key: 'identifier', icon: LocationOnIcon, value: jobIdentifier })}
            {renderRow({
              key: 'address',
              icon: LocationOnIcon,
              value: jobAddressLines.join(', ') || null,
              onActivate: jobAddressLines.length > 0 ? () => openMap(jobAddressLines.join(', ')) : undefined
            })}
          </>
        )
      case 'estimator': {
        // Offset like the row icons, so the 16px avatar centres on the 32px pill beside it.
        const avatar = owner ? (
          <span className={classes.sheetAvatar}>
            <EstimatorAvatar
              user={{ id: owner.id, firstName: owner.firstName, lastName: owner.lastName }}
              userPictures={userPictures}
              width="16px"
              height="16px"
              renderEmpty="blank"
              hideTooltip
              avatarColor={getActivityColor(ownerUser?.user_metadata?.color)}
            />
          </span>
        ) : undefined
        // The name is a dropdown of the account's estimators, so changing one is a single pick rather
        // than a trip through the Change dialog (ENG-2080, the prototype's rep chip).
        const canPickEstimator = estimatorClickable && estimatorOptions.length > 0
        return (
          <>
            {canPickEstimator ? (
              <div key="name" className={classes.sheetRow}>
                {avatar ?? <PersonIcon className={classes.sheetRowIcon} />}
                <button
                  type="button"
                  aria-haspopup="true"
                  className={classnames(classes.sheetPill, classes.sheetPillClickable, classes.sheetDropdown)}
                  onClick={(e) => setEstimatorMenuAnchorEl(e.currentTarget)}
                >
                  <span className={classes.sheetDropdownText}>{estimatorName || 'Assign Estimator'}</span>
                  <ArrowDropDownIcon
                    className={classnames(classes.sheetDropdownCaret, {
                      [classes.sheetDropdownCaretOpen]: !!estimatorMenuAnchorEl
                    })}
                  />
                </button>
              </div>
            ) : (
              renderRow({ key: 'name', leading: avatar, icon: PersonIcon, value: estimatorName })
            )}
            {renderRow({ key: 'title', icon: PeopleIcon, value: owner?.title })}
            {renderRow({
              key: 'email',
              icon: MailIcon,
              value: estimatorEmail,
              onActivate: estimatorEmail ? () => openMailto(estimatorEmail) : undefined
            })}
            {renderRow({
              key: 'phone',
              icon: PhoneIcon,
              value: estimatorPhone,
              onActivate: estimatorPhone ? () => openTel(estimatorPhone) : undefined
            })}
          </>
        )
      }
      default:
        return null
    }
  }

  // One item of the Edit menu.
  const renderAction = (key: string, icon: React.ComponentType<SvgIconProps>, label: string, onClick: () => void) => (
    <MenuItem key={key} icon={icon} onClick={act(onClick)}>
      {label}
    </MenuItem>
  )

  // The same actions the desktop menu offers for this section, minus its tel:/mailto: items — those are
  // the rows above here, so repeating them would list every value twice.
  const renderActions = () => {
    const swapLabel = `Swap${canCreateContact ? ' or Create' : ''} Contact`
    switch (shownSection) {
      case 'contact':
        if (!contactClickable) return []
        return [
          onEditContact && renderAction('edit', EditIcon, 'Edit Contact', onEditContact),
          onSelectContact && renderAction('swap', PeopleIcon, swapLabel, onSelectContact),
          canAddSecondaryContact &&
            renderAction('add-secondary', AddIcon, 'Add Secondary Contact', onSelectSecondaryContact),
          contactDetailsToggle &&
            renderAction(
              'toggle-details',
              contactDetailsToggle.hidden ? VisibilityIcon : VisibilityOffIcon,
              contactDetailsToggle.hidden ? 'Show Email & Phone on Work Order' : 'Hide Email & Phone on Work Order',
              () => contactDetailsToggle.onChange(!contactDetailsToggle.hidden)
            )
        ].filter(Boolean)
      case 'secondary-contact':
        if (!secondaryContactClickable) return []
        return [
          onEditSecondaryContact && renderAction('edit', EditIcon, 'Edit Secondary Contact', onEditSecondaryContact),
          onSelectSecondaryContact && renderAction('swap', PeopleIcon, swapLabel, onSelectSecondaryContact),
          onRemoveSecondaryContact &&
            renderAction('remove', RemoveIcon, 'Remove Secondary Contact', onRemoveSecondaryContact)
        ].filter(Boolean)
      case 'job':
        if (!jobAddressClickable || !onEditJobAddress) return []
        return [renderAction('edit', EditIcon, 'Edit Address', onEditJobAddress)]
      case 'estimator':
        if (!estimatorClickable) return []
        return [
          renderAction('edit', PeopleIcon, isWorkOrder ? 'Change Sales Rep' : 'Change Estimator', onSelectEstimator)
        ]
      // The date is the document sheet's only editable value. It gets a named menu item rather than a
      // bare pencil, so it is clear what the button edits.
      case 'document':
        if (!isEditable || isWorkOrder) return []
        return [
          <MenuItem key="edit-date" icon={EventIcon} onClick={editDate}>
            Edit Created Date
          </MenuItem>
        ]
      default:
        return []
    }
  }

  const actions = renderActions()

  return (
    <>
      <Drawer anchor="bottom" open={Boolean(section)} onClose={onClose} classes={{ paper: classes.sheetPaper }}>
        <div className={classes.sheetHeader}>
          <Typography component="div" className={classes.sheetTitle}>
            {isDocument ? (
              <>
                <span>{docLabel}</span>
                {quoteId && <span className={classes.sheetTitleId}>ID {quoteId}</span>}
              </>
            ) : (
              sectionTitle
            )}
          </Typography>
          <IconButton className={classes.sheetCloseButton} size="small" aria-label="Close" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* The document sheet is the company block the desktop header shows above its columns, dropped
            from the phone card itself. It is read-only detail, so it renders plain lines rather than the
            editable sections' pills, and the date shares its bottom row with the pencil. */}
        {isDocument ? (
          <div className={classes.sheetTray}>
            <div className={classes.sheetRows}>
              <div className={classes.sheetCompanyRow}>
                {logo && <img className={classes.sheetLogo} src={logo} alt={companyName || 'Company logo'} />}
                <Typography component="div" className={classes.sheetCompanyName}>
                  {companyName}
                </Typography>
              </div>
              {renderPlainRow(
                'company-address',
                MetaLocationIcon,
                companyAddress?.length > 0 ? companyAddress.join(', ') : null
              )}
              {renderPlainRow('company-phone', MetaPhoneIcon, companyPhone)}
              {companyAdditional && (
                <div className={classes.additionalRow}>
                  <span className={classnames(classes.tick, classes.additionalTick)} />
                  <Typography component="div" variant="caption" className={classes.additionalText}>
                    {companyAdditional}
                  </Typography>
                </div>
              )}
            </div>
            <div className={classes.sheetDocFooter}>
              {renderPlainRow('date', MetaEventIcon, documentDate)}
              {actions.length > 0 && (
                <IconButton
                  className={classes.sheetEditButton}
                  aria-label="Edit"
                  aria-haspopup="true"
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          </div>
        ) : (
          <div className={classes.sheetTray}>
            <div className={classes.sheetRows}>{renderRows()}</div>
            {actions.length > 0 && (
              <div className={classes.sheetToolbar}>
                <IconButton
                  className={classes.sheetEditButton}
                  aria-label="Edit"
                  aria-haspopup="true"
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* Portalled out of the sheet, so it layers above it rather than inside its scroll area. */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={() => setMenuAnchorEl(null)}>
        {actions}
      </Menu>

      {/* The estimator dropdown. Picking one writes it straight to the quote; the sheet stays open so
          the new rep's email and phone show right away. */}
      <Menu
        anchorEl={estimatorMenuAnchorEl}
        open={Boolean(estimatorMenuAnchorEl)}
        onClose={() => setEstimatorMenuAnchorEl(null)}
      >
        {estimatorOptions.map((user) => {
          const selected = isCurrentEstimator(user)
          return (
            <MenuItem
              key={user.user_id}
              icon={selected ? CheckIcon : undefined}
              selected={selected}
              onClick={() => {
                setEstimatorMenuAnchorEl(null)
                if (!selected) setEstimatorUser(user)
              }}
            >
              {user.name}
            </MenuItem>
          )
        })}
      </Menu>
    </>
  )
}
